<template>
  <div class="autoconnect-performance-report relative text-lg mb-2 flex flex-col justify-center items-center">
    <report-header
      v-if="report"
      :error.sync="error"
      :report="report"
      class="report-header"
      @getReport="getReport"
    >
      <template #title>
        autoconnect performance report
      </template>
      <template #subtitle>
        compare and analyze the performance of your autoconnects in the
        <br />
        last 180 days
      </template>
    </report-header>
    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
      <loading-icon />
    </div>
    <div v-else class="mt-4 lg:-ml-20 w-full">
      <sb-custom-table
        :rows="tableRows"
        :columns="tableColumns"
        :defaultSort="sortDirection"
        :defaultSortColumn="sortColumn"
        @sortChange="updateTableSort"
        disableTableSort
        sortable
        class="custom-table"
      >
        <template #link="item">
          <a :href="'/auto_campaigns/'+item.row['id']">
            +
          </a>
        </template>
      </sb-custom-table>
      <div v-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
        <pagination
          :page.sync="page"
          :pages="pagesTotal"
          :gap="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { subDays } from 'date-fns';
import { mapState } from "vuex";
import { SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { reportsAPI } from '../../services/api/reportsAPI';
import { commafyNumber, truncateWithEllipses } from "../../utilities";
import ReportHeader from "./ReportHeader";

export default {
  name: 'AutoconnectPerformanceReport',
  components: {
    SbCustomTable,
    LoadingIcon,
    Pagination,
    ReportHeader
  },
  data() {
    return {
      page: 0,
      sortColumn: "type",
      sortDirection: "asc",
      report: null,
      tableRows: [],
      loadingReport: false,
      tableColumns: [
        { key: "link", value: "", sortable: false, width:20 },
        { key: "type", value: "type" },
        { key: "name", value: "name" },
        { key: "recipients", value: "recipients" },
        { key: "credit_used", value: "credits used" },
        { key: "cost_of_autoconnect", value: "cost of autoconnect" },
        { key: "clicks", value: "clicks" },
        { key: "visits", value: "visits" },
        { key: "spend", value: "spend" },
      ],
      error: {},
    }
  },
  created() {
    this.$store.dispatch('LOAD_CURRENT_MERCHANT');
    this.getReport();
  },
  watch: {
    page() {
      this.getReport();
    }
  },
  computed: {
    pagesTotal(){
      return this.report?.pagination?.total_pages || 1;
    },
    ...mapState(['currentMerchant'])
  },
  methods: {
    async formatReportData() {
      const results = this.report.output_data.results;
      if (results.length > 0) {
        this.tableRows = results.map(row => {
          const clickCount = Math.round( 10 * Number(row.click_thru_rate)) / 10
          return {
            ...row,
            type: truncateWithEllipses(row.type, 68),
            name: truncateWithEllipses(row.name, 68),
            recipients: commafyNumber(parseInt(row.recipients ?? 0)),
            credit_used: commafyNumber(parseInt(row.credit_used)),
            cost_of_autoconnect: `$${commafyNumber((Math.round(row.cost_of_autoconnect*100)/100).toFixed(2))}`,
            clicks: `${commafyNumber(row.clicks)} (${clickCount}%)`,
            visits: commafyNumber(parseInt(row.visits)),
            spend: `$${commafyNumber(row.spend)}`,
          }
        });
      }else{
        this.tableRows = [];
      }
    },
    async fetchExistingReport() {
      this.loadingReport = true;
      try {
        const { report, pagination } = await reportsAPI.getReportById(
          this.report.id, this.page + 1, {columnKey: this.sortColumn, direction: this.sortDirection}
        );
        this.report = { pagination, ...report };
        this.formatReportData();
      } catch(error){
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingReport = false;
      }
    },
    updateTableSort(sort) {
      if (
        ( this.sortColumn !== sort.column
          || this.sortDirection !== sort.sorting )
        && this.tableRows.length > 0
      ) {
        this.sortColumn = sort.column;
        this.sortDirection = sort.sorting;
        this.page = 0
        this.getReport();
      }
    },
    getReport(newReport) {
      if (!this.report?.id || (this.report.id && newReport)) {
        this.initiateReportJob();
      } else {
        this.fetchExistingReport();
      }
    },
    async initiateReportJob() {
      this.loadingReport = true;
      const report = await reportsAPI.initializeReportJob({
        report: {
          type: 'Reports::AutoconnectPerformanceReport',
        }
      });

      this.reportId = report.id
      this.pollReport();
    },
    pollReport() {
      setTimeout(() => {
        reportsAPI.fetchReport(this.reportId)
          .then(({ report, pagination }) => {
            if (report.state === 'completed') {
              this.report = { pagination, ...report };
              this.formatReportData();
            } else {
              this.pollReport();
            }
          }).catch(error => {
            this.error.message = "error, please refresh or contact support";
          }).finally(() => {
            this.loadingReport = false;
          });
      }, 1000);
    }
  }
}
</script>

<style scoped>
@media only screen and (min-width: 1024px) {
  .custom-table {
    overflow-x: visible;
  }
}
</style>
